import { create } from "zustand";
import {
  mockClients, mockProjects, mockMilestones, mockTasks, mockUpdates,
  mockMembers, mockInvoices, mockQuotes, mockPayments, mockUsers, mockTeamProfiles,
  mockProjectTeam, mockQuoteResources, mockQuoteTeam,
} from "./seed";
import { MOCK_RESOURCES } from "./resources.seed";
import { mockPortfolioItems, mockPortfolioImages } from "./portfolio.seed";
import type { PortfolioItem, PortfolioImage } from "@/os/components/PortfolioItemSheet";
import type {
  ClientRow, ProjectRow, Milestone, Task, ProjectUpdate, ProjectMember,
  Invoice, Quote, Payment, UserWithRoles, TeamProfile, ProjectTeamMember, Resource,
  PaymentMethod, ProjectInvitation, ProjectClientContact, ProjectInvitationStatus,
} from "../types";

type State = {
  clients: ClientRow[];
  projects: ProjectRow[];
  milestones: Milestone[];
  tasks: Task[];
  updates: ProjectUpdate[];
  members: ProjectMember[];
  invoices: Invoice[];
  quotes: Quote[];
  payments: Payment[];
  users: UserWithRoles[];
  teamProfiles: TeamProfile[];
  projectTeam: { id: string; project_id: string; team_profile_id: string; role_on_project: string | null; order_index: number; resource_id?: string | null; monthly_rate_snapshot?: number | null }[];
  quoteResources: { id: string; quote_id: string; resource_id: string | null; role_label: string; quantity: number; unit_price_override: number; justification: string | null; order_index: number }[];
  quoteTeam: { id: string; quote_id: string; team_profile_id: string; role_on_project: string | null; match_reason: string | null; order_index: number }[];
  resources: Resource[];
  projectInvitations: ProjectInvitation[];
  clientContactOverrides: Record<
    string,
    {
      removed?: boolean;
      status?: ProjectInvitationStatus;
      display_name?: string;
      email?: string | null;
      location?: string | null;
      is_primary?: boolean;
      tab_permissions?: ProjectClientContact["tab_permissions"];
    }
  >;
  appSettings: {
    resources_discount_percent: number;
    invoice_settings?: import("../types").InvoiceCompanySettings;
    updated_at?: string;
  };
  paymentMethods: PaymentMethod[];
  portfolioItems: PortfolioItem[];
  portfolioImages: PortfolioImage[];
  reset: () => void;
  set: (patch: Partial<State>) => void;
};

const initial = () => {
  const resources: Resource[] = MOCK_RESOURCES.map((r) => ({
    id: crypto.randomUUID(),
    headline: r.headline,
    headline_es: (r as any).headline_es ?? "",
    division: (r as any).division ?? "operations",
    description_en: r.description_en,
    description_es: r.description_es,
    hourly_rate: r.hourly_rate,
    overtime_hourly_rate: r.overtime_hourly_rate,
    monthly_rate: r.monthly_rate,
    monthly_included_hours: r.monthly_included_hours,
    max_overtime_hours: r.max_overtime_hours,
    currency: "USD",
    is_active: true,
    order_index: r.order_index,
    created_at: new Date().toISOString(),
  })) as Resource[];

  // Lookup table: headline (case-insensitive) → Resource
  const byHeadline = new Map<string, Resource>();
  for (const r of resources) byHeadline.set(r.headline.trim().toLowerCase(), r);

  // Map legacy team-profile headlines onto the curated Smart Canvas catalog.
  const HEADLINE_SYNONYMS: Record<string, string> = {
    "ai engineer": "Artificial Intelligence Specialist",
    "backend engineer": "Full Stack Developer (Backend Focus)",
    "frontend engineer": "Full Stack Developer (Frontend Focus)",
    "mobile engineer": "Full Stack Developer (Mobile Focus)",
    "devops engineer": "Infrastructure & DevOps Specialist",
    "sre": "Infrastructure & DevOps Specialist",
    "cloud architect": "Systems & Architecture Specialist",
    "engineering manager": "CTO",
    "director of programs": "Project Manager",
    "senior project manager": "Project Manager",
    "project manager": "Project Manager",
    "project coordinator": "Functional Project Manager",
    "head of marketing": "CMO",
    "performance marketing manager": "Marketing & Branding Specialist",
    "marketing analyst": "Marketing & Branding Specialist",
    "seo specialist": "Marketing & Branding Specialist",
    "ui designer": "UI/UX Specialist & Designer",
    "senior product designer": "UI/UX Specialist & Designer",
    "motion designer": "UI/UX Specialist & Designer",
    "senior graphic designer": "UI/UX Specialist & Designer",
    "junior graphic designer": "UI/UX Specialist & Designer",
  };
  const resolveResource = (headline: string | null | undefined): Resource | null => {
    if (!headline) return null;
    const key = headline.trim().toLowerCase();
    const direct = byHeadline.get(key);
    if (direct) return direct;
    const synonym = HEADLINE_SYNONYMS[key];
    if (synonym) return byHeadline.get(synonym.toLowerCase()) ?? null;
    return null;
  };

  // Inherit price tier from the Resources catalog by exact headline match.
  const teamProfiles = (structuredClone(mockTeamProfiles) as TeamProfile[]).map((p) => {
    const r = resolveResource(p.headline);
    // All seeded collaborators run under the special usage scheme so the
    // AI Simulations engine can drive their activity in Sandbox.
    return { ...p, resource_id: r?.id ?? null, special_scheme: true };
  });

  // Each project_team row inherits resource + monthly rate from the team profile.
  const projectTeam = (structuredClone(mockProjectTeam) as State["projectTeam"]).map((pt) => {
    const profile = teamProfiles.find((tp) => tp.id === pt.team_profile_id);
    const r = profile ? resolveResource(profile.headline) : null;
    return {
      ...pt,
      resource_id: r?.id ?? null,
      monthly_rate_snapshot: r ? Number(r.monthly_rate ?? 0) : 0,
    };
  });

  return ({
  clients: structuredClone(mockClients),
  projects: structuredClone(mockProjects),
  milestones: structuredClone(mockMilestones),
  tasks: structuredClone(mockTasks),
  updates: structuredClone(mockUpdates),
  members: structuredClone(mockMembers),
  invoices: structuredClone(mockInvoices),
  quotes: structuredClone(mockQuotes),
  payments: structuredClone(mockPayments),
  users: structuredClone(mockUsers),
  teamProfiles,
  projectTeam,
  quoteResources: structuredClone(mockQuoteResources) as State["quoteResources"],
  quoteTeam: structuredClone(mockQuoteTeam) as State["quoteTeam"],
  projectInvitations: [] as ProjectInvitation[],
  clientContactOverrides: {} as State["clientContactOverrides"],
  resources,
  appSettings: {
    resources_discount_percent: 0,
    invoice_settings: {
      commercial_name: "Smart Canvas",
      legal_name: "Smart Canvas Solutions, S.A.",
      tax_id: "99544741",
      address_line1: "Ciudad de Guatemala, Zona 7 de Mixco P7",
      address_line2: "",
      city: "Guatemala",
      state: "",
      postal_code: "01057",
      country: "Guatemala",
      email: "billing@smart-canvas.com",
      phone: "",
      website: "smart-canvas.com",
      logo_data_url: "",
    },
  },
  paymentMethods: [
    {
      id: "pm-bank-bi",
      kind: "bank",
      label: "Banco Industrial · 2880049222",
      currency: "USD",
      details: {
        bank_name: "Banco Industrial",
        account_holder: "Smart Canvas Solutions, S.A.",
        account_number: "2880049222",
        routing_number: "",
        swift: "INDLGTGC",
        iban: "",
      },
      is_active: true,
      is_default_global: true,
      order_index: 0,
      created_at: new Date().toISOString(),
    },
  ] as PaymentMethod[],
  portfolioItems: structuredClone(mockPortfolioItems) as PortfolioItem[],
  portfolioImages: structuredClone(mockPortfolioImages) as PortfolioImage[],
});
};

/**
 * In-memory mock store for the Sandbox environment.
 * Not persisted on purpose — every fresh session starts from seed.
 */
export const useMockStore = create<State>((set) => ({
  ...initial(),
  reset: () => set(initial()),
  set: (patch) => set(patch as any),
}));